import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================
// Shared types
// ============================================================
// Keep these loose — the Phase A backend returns more fields than
// we render. Tightening interfaces can come once the model is stable.

interface Company {
  id: number;
  name: string;
  domain: string | null;
  website: string | null;
  industry: string | null;
  notes: string | null;
  customer_user_id: number | null;
  customer_user_email: string | null;
  customer_user_first_name: string | null;
  customer_user_last_name: string | null;
  stripe_customer_id: string | null;
  source: string;
  contact_count: number;
  deal_count: number;
  deals_won: number;
  deals_lost: number;
  demo_count: number;
  last_demo_at: string | null;
  // Lifetime spend, summed from crm_invoices linked to this company.
  total_paid_pence: number | string; // bigint may come back as string
  invoice_count: number;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDaysAgo = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};
const formatPence = (p: number | string | null | undefined): string => {
  if (p == null) return '£0';
  const n = typeof p === 'string' ? parseInt(p, 10) : p;
  if (!Number.isFinite(n) || n === 0) return '£0';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n / 100);
};

// ============================================================
// Companies list view
// ============================================================

export const CompaniesView: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCustomers, setFilterCustomers] = useState(false);
  const [sort, setSort] = useState<'default' | 'spend'>('spend'); // default to "biggest spenders first"
  const [syncingInvoices, setSyncingInvoices] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (filterCustomers) qs.set('customers', '1');
      if (sort !== 'default') qs.set('sort', sort);
      const res = await fetch(`/api/crm/companies?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load companies');
      const data = await res.json();
      setCompanies(data.companies || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterCustomers, sort]);

  const onSearchSubmit = (e: React.FormEvent) => { e.preventDefault(); load(); };

  const refreshInvoices = async () => {
    setSyncingInvoices(true);
    try {
      const res = await fetch('/api/crm/invoices/sync', { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        const breakdown = `${data.invoices || 0} invoices + ${data.charges || 0} direct charges`;
        window.alert(`Synced ${data.synced} payments (${breakdown}) · ${data.linked} (re)linked to companies.`);
        await load();
      } else {
        window.alert(`Sync failed: ${data.error || 'unknown'}`);
      }
    } finally {
      setSyncingInvoices(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearchSubmit} className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search companies by name or domain…"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </form>
        <select
          value={sort}
          onChange={e => setSort(e.target.value as 'default' | 'spend')}
          className="px-2 py-2 border border-gray-300 rounded text-xs"
        >
          <option value="spend">Sort: Biggest spend</option>
          <option value="default">Sort: Customers first</option>
        </select>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={filterCustomers}
            onChange={e => setFilterCustomers(e.target.checked)}
          />
          <span>Customers only</span>
        </label>
        <button onClick={load} className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
          Search
        </button>
        <button
          onClick={refreshInvoices}
          disabled={syncingInvoices}
          title="Pull latest invoices from both Stripe accounts"
          className="px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {syncingInvoices ? 'Syncing…' : '💷 Sync invoices'}
        </button>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading…</div>
      ) : companies.length === 0 ? (
        <div className="p-8 text-center text-gray-400 italic">No companies match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
              <tr>
                <th className="py-2 px-3">Company</th>
                <th className="py-2 px-3">Domain</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3 text-right">Lifetime spend</th>
                <th className="py-2 px-3 text-right">Contacts</th>
                <th className="py-2 px-3 text-right">Deals</th>
                <th className="py-2 px-3 text-right">Demos</th>
                <th className="py-2 px-3 text-right">Last demo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companies.map(c => {
                const pence = typeof c.total_paid_pence === 'string'
                  ? parseInt(c.total_paid_pence, 10)
                  : (c.total_paid_pence || 0);
                const isBig = pence >= 100000; // £1k+
                return (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/crm/companies/${c.id}`)}
                  >
                    <td className="py-2 px-3 font-semibold text-gray-900">{c.name}</td>
                    <td className="py-2 px-3 text-gray-500">{c.domain || '—'}</td>
                    <td className="py-2 px-3">
                      {c.customer_user_id ? (
                        <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-[10px] font-medium">
                          Customer
                        </span>
                      ) : c.deals_won > 0 ? (
                        <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-medium">
                          Won deal
                        </span>
                      ) : c.deals_lost > 0 ? (
                        <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">
                          Lost
                        </span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium">
                          Prospect
                        </span>
                      )}
                    </td>
                    <td className={`py-2 px-3 text-right ${isBig ? 'font-bold text-green-700' : pence > 0 ? 'text-gray-700' : 'text-gray-300'}`}>
                      {formatPence(pence)}
                      {c.invoice_count > 0 && (
                        <div className="text-[10px] text-gray-400 font-normal">
                          {c.invoice_count} invoice{c.invoice_count === 1 ? '' : 's'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-700">{c.contact_count}</td>
                    <td className="py-2 px-3 text-right text-gray-700">
                      {c.deal_count}
                      {c.deals_won > 0 && <span className="text-[10px] text-green-600 ml-1">{c.deals_won}W</span>}
                      {c.deals_lost > 0 && <span className="text-[10px] text-gray-400 ml-1">{c.deals_lost}L</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-700">{c.demo_count}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-500">{formatDaysAgo(c.last_demo_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
        {companies.length} companies
      </div>
    </div>
  );
};
